import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Col, Row } from "reactstrap";
import MenuItem from "@material-ui/core/MenuItem";

import { history } from "../../utils/History";
import { getRequestErrorMessage } from "../../utils/winnerWorkflow";
import Table from "../../components/table";
import SearchButton from "../../components/buttons/SearchButton";
import ResetFilterButton from "../../components/buttons/ResetFilterButton";
import ActiveFilterChips from "../../components/filters/ActiveFilterChips";
import FilteredEmptyState from "../../components/emptyState/FilteredEmptyState";
import HeaderMenu from "../../components/commons/menu/HeaderMenu";
import OverflowTooltip from "../../components/commons/OverflowTooltip";
import DetailButton from "../../components/table/detailButton";
import { toastError, toastSuccess } from "../../components/commons/toast";
import useLatestRequest from "../../components/hooks/useLatestRequest";
import usePromotionFilterOptions from "../../components/hooks/usePromotionFilterOptions";
import { downloadExcelResponse, getExcelExportErrorMessage } from "../../utils/excelExport";
import Promo from "../../store/actions/promotion/Index";
import SidebarPromo from "./SidebarPromo";
import GenerateModal from "./GenerateModal";
import FilterModal from "./FilterLocationModal";
import {
  createDetailLocation,
  hasNonDefaultQuery,
  pageSizeParam,
  positivePage,
  stringParam,
  useListQueryState,
} from "../../utils/listQueryState";
import CopyableText from "../../components/commons/CopyableText";
import { quotedSearchLabel, summarizeFilterValues } from "../../utils/filterChips";

const PARTICIPANT_QUERY_SCHEMA = {
  page: positivePage,
  length: pageSizeParam,
  search: stringParam(),
  city_id: stringParam(),
  job: stringParam(),
  promo_return: stringParam(),
};

const getParticipantJobParam = (job) =>
  job
    .split(",")
    .map((item) => item.split("~")[1])
    .filter(Boolean)
    .join(",");

const ParticipantList = ({ match }) => {
  const id = match?.params?.id;
  const dispatch = useDispatch();
  const { beginRequest, isLatestRequest } = useLatestRequest();

  const [dataParticipant, setDataParticipant] = useState([]);
  const { state: query, updateQuery, resetQuery, location } = useListQueryState(PARTICIPANT_QUERY_SCHEMA);
  const { page, length, search: searchText, city_id: filterCity, job: filterJob } = query;
  const hasActiveFilters = hasNonDefaultQuery(
    query,
    PARTICIPANT_QUERY_SCHEMA,
    ["search", "city_id", "job"]
  );
  const paramFilterJob = getParticipantJobParam(filterJob);
  const [totalData, setTotalData] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [enableGenerate, setEnableGenerate] = useState(false);
  const [dataPromo, setDataPromo] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [filterModal, setFilterModal] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const generatingRef = useRef(false);
  const exportRequestInFlight = useRef(false);
  const { cityOptions, jobOptions } = usePromotionFilterOptions({ cities: true, jobs: true });

  const resetAllFilters = () => resetQuery(["page", "search", "city_id", "job"]);
  const activeFilterChips = [
    searchText && {
      key: "search",
      label: quotedSearchLabel(searchText),
      onRemove: () => updateQuery({ page: 1, search: "" }),
    },
    filterCity && {
      key: "city_id",
      label: `Kota: ${summarizeFilterValues(filterCity, cityOptions)}`,
      onRemove: () => updateQuery({ page: 1, city_id: "" }),
    },
    filterJob && {
      key: "job",
      label: `Pekerjaan: ${summarizeFilterValues(filterJob, jobOptions)}`,
      onRemove: () => updateQuery({ page: 1, job: "" }),
    },
  ].filter(Boolean);

  const title = dataPromo
    ? `Daftar Peserta Promo ${dataPromo.name}`
    : "Daftar Peserta";

  const handleRefresh = () => {
    const requestId = beginRequest();
    setIsLoading(true);
    setLoadError(null);

    return new Promise((resolve, reject) => {
      dispatch(
        Promo.getParticipant(
          {
            page: page || 1,
            length,
            id,
            search_text: searchText,
            city_id: filterCity,
            job: paramFilterJob,
          },
          resolve,
          reject
        )
      );
    })
      .then((res) => {
        if (!isLatestRequest(requestId)) return;
        setDataParticipant(res?.data ?? []);
        setTotalData(res?.totalCount ?? 0);
        const nextMaxPage = Math.max(1, Math.ceil((res?.totalCount ?? 0) / length));
        setMaxPage(nextMaxPage);
        if (page > nextMaxPage) updateQuery({ page: nextMaxPage });
        setEnableGenerate(Boolean(res?.enable_generate));
      })
      .catch((error) => {
        if (!isLatestRequest(requestId)) return;
        setLoadError(
          getRequestErrorMessage(error, "Data peserta gagal dimuat.")
        );
      })
      .finally(() => {
        if (isLatestRequest(requestId)) setIsLoading(false);
      });
  };

  useEffect(() => {
    handleRefresh();
    // handleRefresh depends on the current filter state by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchText, page, length, filterCity, paramFilterJob]);

  useEffect(() => {
    new Promise((resolve) => {
      dispatch(Promo.detail({ id }, resolve));
    }).then((res) => setDataPromo(res?.data));
  }, [dispatch, id]);

  const handleSearch = (value) => {
    updateQuery({ page: 1, search: value?.length > 0 ? value : "" });
  };

  const handleFilter = (city, job) => {
    setFilterModal(false);
    updateQuery({ page: 1, city_id: city, job });
  };

  const handleExportExcel = async () => {
    if (exportRequestInFlight.current || isLoading || !totalData) return;

    exportRequestInFlight.current = true;
    setIsExporting(true);

    try {
      const response = await dispatch(Promo.exportParticipants({
        id,
        search_text: searchText,
        city_id: filterCity,
        job: paramFilterJob,
      }));
      downloadExcelResponse(response, `peserta-promo-${id}.xlsx`);
      toastSuccess("File Excel peserta berhasil diunduh.");
    } catch (error) {
      const message = await getExcelExportErrorMessage(
        error,
        "Data peserta gagal diekspor."
      );
      toastError(message);
    } finally {
      exportRequestInFlight.current = false;
      setIsExporting(false);
    }
  };

  const handleGenerate = async (param) => {
    if (generatingRef.current) return;

    generatingRef.current = true;
    setGenerateError("");
    setIsGenerating(true);
    try {
      const response = await dispatch(Promo.generateWinner(param));
      setGenerateModal(false);
      history.push({
        pathname: `/participant/generate-winner/${id}`,
        search: location.search,
        state: {
          newData: response?.data ?? [],
          returnTo: `${location.pathname}${location.search}`,
          fromList: true,
        },
      });
    } catch (error) {
      const message = getRequestErrorMessage(
        error,
        "Generate pemenang gagal. Silakan coba lagi."
      );
      setGenerateError(message);
      toastError(message);
    } finally {
      generatingRef.current = false;
      setIsGenerating(false);
    }
  };

  const columns = [
    { Header: "NO", accessor: "no" },
    {
      Header: "NAMA PESERTA",
      accessor: "full_name",
      Cell: ({ value }) => <OverflowTooltip value={value} size="wide" />,
    },
    {
      Header: "NIK",
      accessor: "nik",
      Cell: ({ row }) => row.original.nik ?? "-",
    },
    {
      Header: "NO WHATSAPP",
      accessor: "phone_number",
      Cell: ({ value }) => (
        <CopyableText value={value} label="Nomor WhatsApp" />
      ),
    },
    {
      Header: "LOKASI",
      accessor: "location_participant",
      Cell: ({ row }) => (
        <a
          href={row.original.location_participant}
          target="_blank"
          rel="noopener noreferrer"
        >
          Lihat Lokasi
        </a>
      ),
    },
    {
      Header: "KOTA ASAL",
      accessor: "city_name",
      Cell: ({ value }) => <OverflowTooltip value={value} size="compact" />,
    },
    {
      Header: "PEKERJAAN",
      accessor: "job",
      Cell: ({ value }) => <OverflowTooltip value={value} size="medium" />,
    },
    {
      Header: "",
      accessor: "actions",
      Cell: ({ row }) => (
        <DetailButton>
          <MenuItem
            onClick={() =>
              history.push(createDetailLocation(`/participant/detail/${id}/${row.original.customer_id}`, location))
            }
          >
            <span style={{ fontSize: 14 }}>Detail</span>
          </MenuItem>
        </DetailButton>
      ),
    },
  ];

  return (
    <div className="menu-container">
      <Row>
        <Col md={3} className="sidebar-promo-col">
          <SidebarPromo active="promo-participant" id={id} returnLocation={location} />
        </Col>
        <Col md={9}>
          <HeaderMenu
            title={title}
            toolbarClassName="participant-toolbar"
            secondaryContent={hasActiveFilters ? <ActiveFilterChips chips={activeFilterChips} /> : null}
          >
            <SearchButton placeholder="Cari Peserta" value={searchText} toggle={handleSearch} />
            <button
              type="button"
              className="button rounded-button primary button-default button-export-excel"
              onClick={handleExportExcel}
              disabled={isLoading || isExporting || !totalData}
            >
              {isExporting ? (
                <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Mengekspor...</>
              ) : (
                <><i className="fas fa-file-excel" aria-hidden="true" /> Export Excel</>
              )}
            </button>
            <button
              type="button"
              disabled={!enableGenerate || isGenerating}
              className={`button rounded-button ${
                !enableGenerate || isGenerating
                  ? "disabled"
                  : "primary button-default"
              }`}
              onClick={() => {
                setGenerateError("");
                setGenerateModal(true);
              }}
            >
              <i className="fas fa-sort-alt" />
              {isGenerating ? " Sedang generate..." : " Generate Pemenang"}
            </button>
            <button
              type="button"
              className="button rounded-button primary button-default"
              onClick={() => setFilterModal(true)}
            >
              <i className="fas fa-sort-alt" /> Filter
            </button>
            <ResetFilterButton
              visible={hasActiveFilters}
              onClick={resetAllFilters}
            />
          </HeaderMenu>
          <div className="content-container-side">
            <div className="overflow">
              <Table
                columns={columns}
                data={dataParticipant}
                stickyHeader
                scrollLabel="Daftar peserta"
                loading={isLoading}
                error={loadError}
                onRetry={handleRefresh}
                emptyContent={hasActiveFilters ? <FilteredEmptyState onReset={resetAllFilters} /> : null}
                pagination={{
                  page,
                  totalData,
                  maxPage,
                  toggle: (nextPage) => updateQuery({ page: nextPage }),
                  length,
                  onPageSizeChange: (nextLength) => updateQuery({ page: 1, length: nextLength }),
                }}
              />
            </div>
          </div>
        </Col>
      </Row>

      <GenerateModal
        show={generateModal}
        toggle={() => !isGenerating && setGenerateModal(false)}
        action={handleGenerate}
        type="add"
        id={id}
        isLoading={isGenerating}
        error={generateError}
      />

      <FilterModal
        show={filterModal}
        toggle={() => setFilterModal(false)}
        action={handleFilter}
        id={id}
        filterCity={filterCity}
        filterJob={filterJob}
        cityOptions={cityOptions}
        jobOptions={jobOptions}
      />
    </div>
  );
};

export default ParticipantList;
